import { useQuery } from "@apollo/client";
import { GET_ORDERS_HISTORY } from "../../queries/GetOrdersHistory";
import {
  OrderHistoryItem,
  OrdersHistoryResponse,
  OrdersHistoryVariables,
} from "./OrderHistoryTypes";
import React from "react";
import { OrdersHistoryFilterValue } from "../../components/OrdersHistory/OrdersHistoryFilter/OrdersHistoryFilter";

const PAGE_SIZE = 20;

const getDateFromFilter = (filter: OrdersHistoryFilterValue) => {
  const d = new Date();

  switch (filter) {
    case "last3months":
      d.setMonth(d.getMonth() - 3);
      return d.toISOString();
    case "last6months":
      d.setMonth(d.getMonth() - 6);
      return d.toISOString();
    case "last12months":
      d.setFullYear(d.getFullYear() - 1);
      return d.toISOString();
    case "all":
      return undefined;
  }
};

export const useOrdersHistory = (filter: OrdersHistoryFilterValue) => {
  const afterDate = React.useMemo(() => getDateFromFilter(filter), [filter]);

  const [items, setItems] = React.useState<OrderHistoryItem[]>([]);
  const [skip, setSkip] = React.useState(0);
  const [isFetchingMore, setIsFetchingMore] = React.useState(false);
  React.useEffect(() => {
    setItems([]);
    setSkip(0);
  }, [filter]);

  const {
    data,
    loading: isLoading,
    error,
    fetchMore,
  } = useQuery<OrdersHistoryResponse, OrdersHistoryVariables>(
    GET_ORDERS_HISTORY,
    {
      variables: {
        options: {
          skip: 0,
          take: PAGE_SIZE,
          filter: {
            ...(afterDate && { createdAt: { after: afterDate } }),
            state: { notEq: "AddingItems" },
          },
        },
      },
      onCompleted: (result) => {
        // First page load → set items
        if (skip === 0) {
          setItems(result?.activeCustomer?.orders?.items);
        }
      },
    }
  );

  const totalItems = data?.activeCustomer?.orders.totalItems ?? 0;

  const loadMore = async () => {
    if (isFetchingMore) return; // Prevent double clicks
    setIsFetchingMore(true); // Start loading-more state

    const newSkip = skip + PAGE_SIZE;

    const fetchResult = await fetchMore({
      variables: {
        options: {
          skip: newSkip,
          take: PAGE_SIZE,
          filter: {
            ...(afterDate && { createdAt: { after: afterDate } }),
            state: { notEq: "AddingItems" },
          },
        },
      },
    });

    const newItems = fetchResult?.data?.activeCustomer?.orders.items ?? [];

    setItems((prev) => [...prev, ...newItems]);

    setSkip(newSkip);

    setIsFetchingMore(false);
  };

  const canLoadMore = items.length < totalItems;

  return {
    isLoading,
    isFetchingMore,
    error,
    items,
    totalItems,
    canLoadMore,
    loadMore,
  };
};
